import React from 'react';

/**
 * Composant réutilisable pour afficher une barre de progression d'objectif annuel
 * avec couleurs et indicateurs visuels style Strava
 */
const AnnualProgressBar = ({
  progressData,
  showLegend = true,
  size = 'normal',
  showTooltip = true
}) => {
  if (!progressData || !progressData.hasTarget) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-50">
        {progressData?.message || 'Pas d\'objectif'}
      </span>
    );
  }

  // Calculer le pourcentage attendu à la date courante
  const expectedPercentage = (progressData.expectedVisitsToDate / progressData.annualTarget) * 100;

  // Déterminer la couleur de la barre selon le statut avec tolérance de ±1 visite
  let barColor;
  const difference = progressData.totalVisitsIncludingPlanned - progressData.expectedVisitsToDate;

  if (difference <= -1) {
    barColor = 'bg-red-400'; // En retard de plus d'1 visite (rouge)
  } else if (difference >= 1) {
    barColor = 'bg-orange-400'; // En avance de plus d'1 visite (orange)
  } else {
    barColor = 'bg-green-400'; // Dans la tolérance ±1 visite (vert)
  }

  // Styles selon la taille
  const heightClass = size === 'small' ? 'h-2' : 'h-3';
  const textSizeClass = size === 'small' ? 'text-xs' : 'text-xs';

  return (
    <div className="flex flex-col space-y-1">
      <div className="flex items-center space-x-2">
        <div className={`flex-1 bg-gray-200 rounded-full ${heightClass} relative`}>
          {/* Barre de progression principale */}
          <div
            className={`${heightClass} rounded-full ${barColor}`}
            style={{ width: `${Math.min(100, progressData.progressPercentage)}%` }}
          ></div>

          {/* Trait pour montrer où on devrait être à la date courante */}
          <div
            className={`absolute top-0 ${heightClass} w-0.5 bg-blue-500`}
            style={{ left: `${Math.min(100, expectedPercentage)}%` }}
            title={showTooltip ? `Attendu à ce jour: ${progressData.expectedVisitsToDate.toFixed(1)} visites` : undefined}
          ></div>

          {/* Indicateur de 100% style Strava si on dépasse */}
          {progressData.progressPercentage > 100 && (
            <div
              className={`absolute top-0 ${heightClass} w-0.5 bg-gray-700`}
              style={{ left: `${(100 / progressData.progressPercentage) * 100}%` }}
              title={showTooltip ? "Objectif annuel (100%)" : undefined}
            ></div>
          )}
        </div>
        <span className={`${textSizeClass} text-gray-600 min-w-0 font-mono font-semibold`}>
          {progressData.totalVisitsIncludingPlanned}/{progressData.annualTarget}
        </span>
      </div>

      {/* Légende visuelle compacte */}
      {showLegend && (
        <div className={`flex items-center space-x-3 ${textSizeClass} text-gray-500`}>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-0.5 bg-blue-500"></div>
            <span>Attendu</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-0.5 bg-gray-700"></div>
            <span>Objectif</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnualProgressBar;
